import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  IconButton,
  Divider,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import FolderIcon from "@mui/icons-material/Folder";
import CategoryIcon from "@mui/icons-material/Category";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { apiClient } from "../../services/api-client";

interface SearchResult {
  type: 'participant' | 'case' | 'program';
  id: string;
  title: string;
  subtitle?: string;
  path: string;
}

const typeIcons = {
  participant: <PersonIcon />,
  case: <FolderIcon />,
  program: <CategoryIcon />,
};

const typeColors = {
  participant: "primary",
  case: "secondary",
  program: "success",
} as const;

const typeLabels = {
  participant: "Participant",
  case: "Case",
  program: "Program",
};

export function SearchResultsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryParam = searchParams.get("q") || "";

  const [query, setQuery] = useState(queryParam);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    if (queryParam) {
      performSearch(queryParam);
    }
  }, [queryParam]);

  const performSearch = async (searchQuery: string) => {
    if (searchQuery.trim().length < 2) {
      setError("Please enter at least 2 characters to search");
      setResults([]);
      return;
    }

    setLoading(true);
    setError(null);
    setHasSearched(true);

    try {
      const { data } = await apiClient.get<{
        status: string;
        data: { results: SearchResult[]; count: number };
      }>(`/search?q=${encodeURIComponent(searchQuery)}&limit=50`);

      setResults(data.data.results);
    } catch (err) {
      console.error("Search error:", err);
      setError("Failed to perform search. Please try again.");
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      setSearchParams({ q: query });
    }
  };

  const handleResultClick = (result: SearchResult) => {
    navigate(result.path);
  };

  // Group results by type
  const groupedResults = results.reduce(
    (acc, result) => {
      if (!acc[result.type]) {
        acc[result.type] = [];
      }
      acc[result.type].push(result);
      return acc;
    },
    {} as Record<string, SearchResult[]>
  );

  return (
    <Box sx={{ py: 3 }}>
      <Typography variant="h4" gutterBottom fontWeight={600}>
        Search
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <form onSubmit={handleSearch}>
            <TextField
              fullWidth
              placeholder="Search participants, cases, or programs..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              autoFocus
            />
          </form>
        </CardContent>
      </Card>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {!loading && hasSearched && results.length === 0 && (
        <Card>
          <CardContent>
            <Box sx={{ textAlign: "center", py: 4 }}>
              <SearchIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No results found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try searching with different keywords
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {!loading && results.length > 0 && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Found {results.length} result{results.length !== 1 ? "s" : ""} for "{queryParam}"
          </Typography>

          <Stack spacing={2}>
            {Object.entries(groupedResults).map(([type, typeResults]) => (
              <Card key={type}>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    {typeIcons[type as keyof typeof typeIcons]}
                    <Typography variant="h6" sx={{ ml: 1, fontWeight: 600 }}>
                      {typeLabels[type as keyof typeof typeLabels]}s
                    </Typography>
                    <Chip
                      label={typeResults.length}
                      size="small"
                      color={typeColors[type as keyof typeof typeColors]}
                      sx={{ ml: 1 }}
                    />
                  </Box>

                  <Stack divider={<Divider />} spacing={0}>
                    {typeResults.map((result) => (
                      <Box
                        key={`${result.type}-${result.id}`}
                        sx={{
                          py: 2,
                          px: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          cursor: "pointer",
                          borderRadius: 1,
                          transition: "background-color 0.2s",
                          "&:hover": {
                            backgroundColor: "action.hover",
                          },
                        }}
                        onClick={() => handleResultClick(result)}
                      >
                        <Box>
                          <Typography variant="body1" fontWeight={600}>
                            {result.title}
                          </Typography>
                          {result.subtitle && (
                            <Typography variant="body2" color="text.secondary">
                              {result.subtitle}
                            </Typography>
                          )}
                        </Box>
                        <IconButton size="small">
                          <ArrowForwardIcon />
                        </IconButton>
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </Box>
      )}

      {!loading && !hasSearched && (
        <Card>
          <CardContent>
            <Box sx={{ textAlign: "center", py: 4 }}>
              <SearchIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Start searching
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Enter a search term to find participants, cases, or programs
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
